'use client';

import { useRef, useState } from 'react';
import type { WebhookRequestRow } from '@capture/shared';
import './WebhookRequestTable.css';

export interface WebhookRequestTableProps {
  rows: WebhookRequestRow[];
  /** Form field names offered by the click-to-insert token picker — mirrors
   *  the `fieldNames` prop every other BehaviorCard body already takes
   *  (ComputeBody, SubstituteBody, TransformBody). Clicking one inserts
   *  `{fieldName}` into whichever value cell most recently had focus, the
   *  same pattern TokenCatalogPanel uses for display-template tokens. */
  fieldNames: string[];
  onChange: (rows: WebhookRequestRow[]) => void;
}

/** Request-body builder for the webhook DERIVATION behavior (transform-webhook
 * T5). Each row is a `{ key, template }` pair — `key` is the outbound JSON
 * field name, `template` is free text that may contain `{fieldName}` tokens
 * resolved by `resolveFieldTokens` (webhook-transform.ts) at run time. */
export function WebhookRequestTable({ rows, fieldNames, onChange }: WebhookRequestTableProps) {
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const valueRefs = useRef<Array<HTMLInputElement | null>>([]);

  function handleKeyChange(index: number, key: string) {
    onChange(rows.map((r, i) => (i === index ? { ...r, key } : r)));
  }

  function handleValueChange(index: number, template: string) {
    onChange(rows.map((r, i) => (i === index ? { ...r, template } : r)));
  }

  // `focusedIndex` is a raw array index, so ANY structural change (add or
  // remove — a remove shifts every later row's index) can leave it pointing
  // at the wrong row. Rather than try to re-map it, clear the focus tracking
  // on every structural change: the "Insert field…" affordance disables until
  // the user clicks a value cell again, which can never misdirect an insert.
  function clearFocus() {
    setFocusedIndex(null);
    // Close the picker too — an already-open panel would otherwise keep
    // showing clickable field buttons that every silently no-op post-clear.
    setPickerOpen(false);
  }

  function handleAddRow() {
    clearFocus();
    onChange([...rows, { key: '', template: '' }]);
  }

  function handleRemoveRow(index: number) {
    clearFocus();
    onChange(rows.filter((_, i) => i !== index));
  }

  function handleInsertToken(name: string) {
    if (focusedIndex === null) return;
    const row = rows[focusedIndex];
    if (!row) return;
    const insertion = `{${name}}`;
    const input = valueRefs.current[focusedIndex];
    let next: string;
    if (input && typeof input.selectionStart === 'number' && typeof input.selectionEnd === 'number') {
      const start = input.selectionStart;
      const end = input.selectionEnd;
      next = row.template.slice(0, start) + insertion + row.template.slice(end);
    } else {
      next = row.template + insertion;
    }
    handleValueChange(focusedIndex, next);
  }

  return (
    <div className="webhook-request-table" data-testid="webhook-request-table">
      <div className="webhook-request-table__toolbar">
        <span className="webhook-request-table__title">Request body</span>
        <button
          type="button"
          onClick={() => setPickerOpen((open) => !open)}
          disabled={fieldNames.length === 0 || focusedIndex === null}
          title={focusedIndex === null ? 'Click a value cell first' : undefined}
          data-testid="webhook-request-insert-toggle"
        >
          Insert field…
        </button>
      </div>

      {pickerOpen && (
        <div className="webhook-request-table__picker" data-testid="webhook-request-field-picker">
          {fieldNames.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => handleInsertToken(name)}
              data-testid={`webhook-request-insert-${name}`}
            >
              {`{${name}}`}
            </button>
          ))}
        </div>
      )}

      <table className="webhook-request-table__grid">
        <thead>
          <tr>
            <th>Key</th>
            <th>Value</th>
            <th aria-label="Remove row" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={i}>
              <td>
                <input
                  value={row.key}
                  onChange={(e) => handleKeyChange(i, e.target.value)}
                  placeholder="key"
                  aria-label={`Request key ${i + 1}`}
                  data-testid={`webhook-request-key-${i}`}
                />
              </td>
              <td>
                <input
                  ref={(el) => {
                    valueRefs.current[i] = el;
                  }}
                  value={row.template}
                  onChange={(e) => handleValueChange(i, e.target.value)}
                  onFocus={() => setFocusedIndex(i)}
                  placeholder="literal text or {fieldName}"
                  aria-label={`Request value ${i + 1}`}
                  data-testid={`webhook-request-value-${i}`}
                />
              </td>
              <td>
                <button
                  type="button"
                  className="webhook-request-table__remove-btn"
                  onClick={() => handleRemoveRow(i)}
                  aria-label={`Remove request row ${i + 1}`}
                  data-testid={`webhook-request-remove-${i}`}
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button
        type="button"
        className="webhook-request-table__add-btn"
        onClick={handleAddRow}
        data-testid="webhook-request-add-row"
      >
        + Add row
      </button>
    </div>
  );
}
